import {
  DEFAULT_COLOR_MODE,
  DEFAULT_FIRST_DAY_OF_WEEK,
  DEFAULT_ISO639_1,
  DEFAULT_RECEIVE_CANCELLED_RESERVATION_EMAILS,
  DEFAULT_RECEIVE_NEW_RESERVATION_EMAILS,
  DEFAULT_RECEIVE_UPDATED_RESERVATION_EMAILS,
} from "@/constants";
import { VenuePortalUser, VenuePortalUserSettings } from "@/graphql/operations";
import { Localization } from "@/i18n/types";
import { getJwtTokenUserId } from "@/tools/jwtToken/jwtToken";
import { ReservationEmailSetting } from "./reservationEmailSettings";
import {
  LocalStorageKey,
  getLocalStorage,
  setLocalStorage,
} from "@/tools/localStorage/localStorage";
import { ColorMode } from "@/types";
import { useMutation, useQuery } from "@apollo/client";
import { SetStateAction, createContext, useCallback, useState } from "react";
import {
  EDIT_VENUE_PORTAL_USER_SETTINGS_MUTATION,
  EditVenuePortalUserSettingsMutationInput,
  EditVenuePortalUserSettingsMutationResponse,
} from "./mutations/editVenuePortalUserSettingsMutation";
import {
  GET_VENUE_PORTAL_USER_SETTINGS_QUERY,
  GetVenuePortalUserSettingsInput,
  GetVenuePortalUserSettingsResponse,
} from "./queries/getVenuePortalUserSettingsQuery";

const defaultSettings = {
  iso639_1: DEFAULT_ISO639_1,
  theme: DEFAULT_COLOR_MODE,
  receiveNewReservationEmails: DEFAULT_RECEIVE_NEW_RESERVATION_EMAILS,
  receiveUpdatedReservationEmails: DEFAULT_RECEIVE_UPDATED_RESERVATION_EMAILS,
  receiveCancelledReservationEmails:
    DEFAULT_RECEIVE_CANCELLED_RESERVATION_EMAILS,
  firstDayOfWeek: DEFAULT_FIRST_DAY_OF_WEEK,
} as const satisfies Partial<VenuePortalUserSettings>;

function useSettingsValues() {
  const [userSettingsId, setUserSettingsId] = useState<
    VenuePortalUser["id"] | null
  >(getJwtTokenUserId() ?? null);

  const [settings, setSettings] = useState<Partial<VenuePortalUserSettings>>({
    iso639_1: getLocalStorage("iso639_1") ?? defaultSettings.iso639_1,
    theme: getLocalStorage("colorMode") ?? defaultSettings.theme,
    receiveNewReservationEmails:
      getLocalStorage("receiveNewReservationEmails", { booleanCast: true }) ??
      defaultSettings.receiveNewReservationEmails,
    receiveUpdatedReservationEmails:
      getLocalStorage("receiveUpdatedReservationEmails", {
        booleanCast: true,
      }) ?? defaultSettings.receiveUpdatedReservationEmails,
    receiveCancelledReservationEmails:
      getLocalStorage("receiveCancelledReservationEmails", {
        booleanCast: true,
      }) ?? defaultSettings.receiveCancelledReservationEmails,
    firstDayOfWeek:
      getLocalStorage("firstDayOfWeek") ?? defaultSettings.firstDayOfWeek,
  });

  useQuery<GetVenuePortalUserSettingsResponse, GetVenuePortalUserSettingsInput>(
    GET_VENUE_PORTAL_USER_SETTINGS_QUERY,
    {
      variables: {
        venuePortalUserId: userSettingsId ?? "",
      },
      onError(error) {
        console.error(error.message);
      },
      onCompleted(data) {
        setSettings({
          ...defaultSettings,
          ...{
            localization: getLocalStorage("iso639_1") ?? undefined,
            theme: getLocalStorage("colorMode") ?? undefined,
            firstDayOfWeek: getLocalStorage("firstDayOfWeek") ?? undefined,
          },
          ...data.venuePortalUser.settings,
        });
      },
      skip: !userSettingsId,
    },
  );

  const [editVenuePortalUserSettingsMutation] = useMutation<
    EditVenuePortalUserSettingsMutationResponse,
    EditVenuePortalUserSettingsMutationInput
  >(EDIT_VENUE_PORTAL_USER_SETTINGS_MUTATION);

  const setSetting = useCallback(
    <T, K extends LocalStorageKey | undefined>(
      settingsKey: keyof NonNullable<typeof settings>,
      fallback: K extends undefined
        ? T
        : T extends string | boolean
          ? T
          : never,
      arg: SetStateAction<T>,
      localStorageKey?: K,
    ) => {
      const storedValue = localStorageKey
        ? getLocalStorage(localStorageKey)
        : null;

      const storedSetting =
        storedValue === null
          ? undefined
          : typeof fallback === "boolean"
            ? storedValue === "true"
            : storedValue;

      const prevSetting =
        (settings?.[settingsKey] as T) ??
        (storedSetting as T | undefined) ??
        fallback;

      const nextSetting =
        typeof arg === "function"
          ? (arg as (prevState: T) => T)(prevSetting)
          : arg;

      if (localStorageKey)
        setLocalStorage(localStorageKey, String(nextSetting));

      setSettings((settings) => ({
        ...settings,
        [settingsKey]: nextSetting,
      }));

      if (!userSettingsId) return;

      editVenuePortalUserSettingsMutation({
        variables: {
          venuePortalUserId: userSettingsId,
          [settingsKey]: nextSetting,
        },
        update(cache) {
          cache.modify({
            id: cache.identify({
              __typename: "VenuePortalUser",
              id: userSettingsId,
            }),
            fields: {
              settings: (prevSettings) => ({
                ...prevSettings,
                [settingsKey]: nextSetting,
              }),
            },
          });
        },
      });
    },
    [settings, userSettingsId, editVenuePortalUserSettingsMutation],
  );

  const setColorMode = useCallback(
    (arg: SetStateAction<ColorMode>) =>
      setSetting("theme", defaultSettings.theme, arg, "colorMode"),
    [setSetting],
  );
  const setLocalization = useCallback(
    (arg: SetStateAction<Localization>) =>
      setSetting("iso639_1", defaultSettings.iso639_1, arg, "iso639_1"),
    [setSetting],
  );
  const toggleReservationEmailSetting = useCallback(
    (settingsKey: ReservationEmailSetting) =>
      setSetting(
        settingsKey,
        defaultSettings[settingsKey],
        (currentSetting: boolean) => !currentSetting,
        settingsKey,
      ),
    [setSetting],
  );
  const setFirstDayOfWeek = useCallback(
    (arg: SetStateAction<string>) =>
      setSetting(
        "firstDayOfWeek",
        defaultSettings.firstDayOfWeek,
        arg,
        "firstDayOfWeek",
      ),
    [setSetting],
  );

  return {
    setColorMode,
    setLocalization,
    settings,
    toggleReservationEmailSetting,
    setFirstDayOfWeek,
    setUserSettingsId,
  };
}

export const SettingsContext = createContext<ReturnType<
  typeof useSettingsValues
> | null>(null);

export default function SettingsProvider({
  children,
}: React.PropsWithChildren) {
  return (
    <SettingsContext.Provider value={useSettingsValues()}>
      {children}
    </SettingsContext.Provider>
  );
}
